import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, L, Check, Modal, money } from "./inputs";

const SCOPES = [["all", "Everything"], ["store", "All store items"], ["tickets", "All event tickets"]];
const EMPTY = { name: "", type: "percent", value: 20, scope: "store", starts_at: "", ends_at: "", active: true };

export const DiscountsTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/discounts").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load sales"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, value: Number(editing.value || 0) };
    try {
      if (editing.id) await api.put(`/admin/discounts/${editing.id}`, body);
      else await api.post("/admin/discounts", body);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (d) => { if (!window.confirm("Delete this sale?")) return; try { await api.delete(`/admin/discounts/${d.id}`); load(); } catch { toast.error("Delete failed"); } };
  const set = (k, v) => setEditing({ ...editing, [k]: v });
  const isLive = (d) => {
    const n = new Date().toISOString().slice(0, 19);
    return d.active && (!d.starts_at || n >= d.starts_at) && (!d.ends_at || n <= (d.ends_at.length <= 10 ? d.ends_at + "T23:59:59" : d.ends_at));
  };

  return (
    <div data-testid="discounts-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-2xl">Scheduled Sales</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-discount-btn" className="inline-flex items-center gap-2 rounded-full bg-[#E63946] px-5 py-2.5 text-sm hover:bg-[#BA1B25]"><Plus size={16} /> New Sale</button>
      </div>
      <p className="text-sm text-white/40 mb-6">Automatic discounts that turn on and off by themselves — no promo code needed. Example: "20% off all shirts this weekend."</p>
      {!items ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} data-testid="discount-row" className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-lg p-3">
              <CalendarClock size={18} className={isLive(d) ? "text-emerald-400 shrink-0" : "text-white/30 shrink-0"} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{d.name} {isLive(d) ? <span className="text-xs text-emerald-400 ml-1">LIVE NOW</span> : !d.active ? <span className="text-xs text-white/30 ml-1">(off)</span> : <span className="text-xs text-white/30 ml-1">(scheduled)</span>}</p>
                <p className="text-sm text-white/40 truncate">{d.type === "percent" ? `${d.value}% off` : `${money(d.value)} off each`} · {SCOPES.find((s) => s[0] === d.scope)?.[1]}{d.starts_at ? ` · from ${d.starts_at.slice(0, 10)}` : ""}{d.ends_at ? ` · until ${d.ends_at.slice(0, 10)}` : ""}</p>
              </div>
              <button onClick={() => setEditing({ ...EMPTY, ...d })} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10"><Pencil size={15} /></button>
              <button onClick={() => del(d)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-[#E63946]"><Trash2 size={15} /></button>
            </div>
          ))}
          {items.length === 0 && <p className="text-white/40 py-16 text-center">No scheduled sales. Create one and it activates automatically on its start date.</p>}
        </div>
      )}
      {editing && (
        <Modal title={editing.id ? "Edit Sale" : "New Scheduled Sale"} onClose={() => setEditing(null)} onSave={save}>
          <div><L>Sale Name</L><input className={inp} data-testid="discount-name-input" value={editing.name} onChange={(e) => set("name", e.target.value)} placeholder="Weekend Shirt Sale" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><L>Type</L><select className={inp} value={editing.type} onChange={(e) => set("type", e.target.value)}><option value="percent">Percentage off</option><option value="fixed">Fixed $ off per item</option></select></div>
            <div><L>{editing.type === "percent" ? "Percent (%)" : "Dollars ($)"}</L><input type="number" className={inp} data-testid="discount-value-input" value={editing.value} onChange={(e) => set("value", e.target.value)} /></div>
            <div className="col-span-2"><L>Applies To</L><select className={inp} value={editing.scope} onChange={(e) => set("scope", e.target.value)}>{SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><L>Starts</L><input type="datetime-local" className={inp} value={editing.starts_at || ""} onChange={(e) => set("starts_at", e.target.value)} /></div>
            <div><L>Ends</L><input type="datetime-local" className={inp} value={editing.ends_at || ""} onChange={(e) => set("ends_at", e.target.value)} /></div>
          </div>
          <Check label="Active" checked={editing.active} onChange={(v) => set("active", v)} />
        </Modal>
      )}
    </div>
  );
};
